import React from "react";

/** Named accent → value text class (overridable via ``valueClassName``). */
export const METRIC_TILE_ACCENTS = {
  overtime: "text-amber-600",
  doubletime: "text-yellow-600",
};

/**
 * Resolve the value text class for a metric tile.
 *
 * @param {string} [accent] - Named accent key (e.g. ``overtime``).
 * @param {string} [valueClassName] - Explicit value class override.
 * @returns {string}
 */
function resolveValueClassName(accent, valueClassName) {
  if (valueClassName) return valueClassName;
  if (accent && METRIC_TILE_ACCENTS[accent]) return METRIC_TILE_ACCENTS[accent];
  return "text-gray-950";
}

/**
 * Render a single metric tile (value + label).
 *
 * @param {Object} props - Component props.
 * @param {string|number} props.value - Primary metric value.
 * @param {string} props.label - Secondary metric label.
 * @param {string} [props.accent] - Named accent (``overtime``, ``doubletime``).
 * @param {string} [props.valueClassName] - Override for the value text classes.
 * @param {string} [props.labelClassName] - Override for the label text classes.
 * @param {string} [props.className] - Extra classes on the tile container.
 * @param {boolean} [props.hero=false] - Emphasized primary metric styling.
 * @param {boolean} [props.supporting=false] - Breakdown tile under a hero (non-bold value).
 * @returns {JSX.Element}
 */
export const MetricTile = ({
  value,
  label,
  accent,
  valueClassName,
  labelClassName = "",
  className = "",
  hero = false,
  supporting = false,
}) => (
  <div
    className={`${
      hero
        ? "rounded-lg border border-gray-300 bg-white px-3 py-1"
        : "rounded-lg border border-gray-200 bg-gray-50 px-3 py-1 shadow-sm"
    } ${className}`}
  >
    <div
      className={`font-bold tracking-tight ${
        supporting ? "text-[1.125rem]" : "text-xl"
      } ${resolveValueClassName(accent, valueClassName)}`}
    >
      {value}
    </div>
    <div
      className={`text-xs font-medium uppercase tracking-wide text-gray-500 ${labelClassName}`}
    >
      {label}
    </div>
  </div>
);

/**
 * Shared row of metric tiles for overview / ticket summary cards.
 *
 * Accepts any count of metrics and keeps them on one row until the container is
 * narrow (``min-w`` on each tile). Per-tile overrides: ``accent``,
 * ``valueClassName``, ``labelClassName``, ``className``.
 *
 * When ``emphasizeHero`` is set and there are 2+ metrics, the first tile is
 * styled as the primary statistic; the rest sit in a divider-grouped flex row
 * (no nested card).
 *
 * @param {Object} props - Component props.
 * @param {Array<{
 *   value: string|number|React.ReactNode,
 *   label: string,
 *   key?: string,
 *   accent?: string,
 *   valueClassName?: string,
 *   labelClassName?: string,
 *   className?: string,
 *   sizeToContent?: boolean,
 * }>} props.metrics - Metric definitions. ``sizeToContent`` tiles size to their
 *   value width; remaining tiles share leftover space evenly (``flex-1``).
 * @param {string} [props.className] - Extra classes on the row wrapper.
 * @param {string} [props.tileClassName] - Classes applied to every tile.
 * @param {boolean} [props.emphasizeHero=false] - Hero + supporting-group layout.
 * @returns {JSX.Element|null}
 */
const MetricTiles = ({
  metrics = [],
  className = "",
  tileClassName = "",
  emphasizeHero = false,
}) => {
  if (!metrics.length) return null;

  /**
   * Layout classes for a single tile in the flat (non-hero) row.
   *
   * @param {Object} metric
   * @param {boolean} hasContentSized
   * @returns {string}
   */
  const layoutClassFor = (metric, hasContentSized) => {
    if (metric.className) return metric.className;
    if (metric.sizeToContent) {
      return "flex-none w-max max-w-full shrink-0";
    }
    if (hasContentSized) return "min-w-0 flex-1";
    return "min-w-[5.5rem] flex-1";
  };

  /**
   * Render one metric tile.
   *
   * @param {Object} metric
   * @param {number} index
   * @param {string} layoutClass
   * @param {{hero?: boolean, supporting?: boolean}} [options]
   * @returns {JSX.Element}
   */
  const renderTile = (
    metric,
    index,
    layoutClass,
    { hero = false, supporting = false } = {}
  ) => (
    <MetricTile
      key={metric.key ?? metric.label ?? index}
      value={metric.value}
      label={metric.label}
      accent={metric.accent}
      valueClassName={metric.valueClassName}
      labelClassName={metric.labelClassName}
      hero={hero}
      supporting={supporting}
      className={`${tileClassName} ${layoutClass}`.trim()}
    />
  );

  if (emphasizeHero && metrics.length > 1) {
    const [heroMetric, ...supporting] = metrics;
    return (
      <div className={`flex items-stretch gap-3 ${className}`}>
        {renderTile(heroMetric, 0, "min-w-[7.5rem] shrink-0 sm:min-w-[9rem]", {
          hero: true,
        })}
        {/* Divider groups supporting metrics without another card shell. */}
        <div className="flex min-w-0 flex-1 flex-wrap items-stretch gap-2 border-l border-gray-200 pl-3">
          {supporting.map((metric, index) =>
            renderTile(
              metric,
              index + 1,
              metric.className || "min-w-[5.5rem] flex-1",
              { supporting: true }
            )
          )}
        </div>
      </div>
    );
  }

  const hasContentSized = metrics.some((metric) => metric.sizeToContent);

  return (
    <div
      className={`flex gap-2 ${hasContentSized ? "flex-nowrap" : "flex-wrap"} ${className}`}
    >
      {metrics.map((metric, index) =>
        renderTile(metric, index, layoutClassFor(metric, hasContentSized))
      )}
    </div>
  );
};

export default MetricTiles;
